/* External */
import React, {
  createContext, useContext, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import jwtDecode from 'jwt-decode';
import { useSelector } from 'react-redux';

/* Ott-common */
import MqttComponent from '@dtvgo/mqtt';
import { gtmEvent } from '@dtvgo/gtm-event-report';

import { useGtmContext } from '../GtmContext';

/* Other */
import {
  APP_THEME,
  ENVIRONMENT,
  MQTT_FLAG,
  MQTT_REALTIME_ENTITLEMENT_FLAG,
  SERVICES_CLIENT_ID,
  SERVICES_CLIENT_ID_PREPROD,
  getAuthTokensData,
  isUserLoggedIn,
} from '../../utils';
import {
  authInterceptor,
  refreshIdToken,
} from '../../services/auth';
import { gtmMqttDimensions } from '../GtmContext/dimensions/mqtt';
import {
  formatMqttDate,
  truncatePayload,
  incrementMqttAttempt,
  resetMqttAttempt,
  normalizeEventData,
} from './mqttGtmHelpers';

const MqttContext = createContext();

const lastEventDispatches = new Map();
const DEBOUNCE_TIME_MS = 500;

const dispatchMqttEvent = (eventConfig, eventData, gtmUserData) => {
  if (!gtmUserData || !eventConfig) {
    console.warn('MQTT GTM Event: Missing gtmUserData or eventConfig', { gtmUserData: !!gtmUserData, eventConfig: !!eventConfig });
    return;
  }

  if (!eventConfig.hash || !eventConfig.eventType || !Array.isArray(eventConfig.dimensions)) {
    console.error('MQTT GTM Event: Invalid eventConfig', eventConfig);
    return;
  }

  if (!gtmUserData.v_user_id || !gtmUserData.v_app_name) {
    console.warn('MQTT GTM Event: gtmUserData incomplete, skipping event', {
      hash: eventConfig.hash,
      hasUserId: !!gtmUserData.v_user_id,
      hasAppName: !!gtmUserData.v_app_name,
      gtmUserData,
    });
    return;
  }

  const eventKey = eventConfig.hash;
  const now = Date.now();
  const lastDispatch = lastEventDispatches.get(eventKey);

  if (lastDispatch && (now - lastDispatch) < DEBOUNCE_TIME_MS) {
    return;
  }

  lastEventDispatches.set(eventKey, now);

  try {
    const normalizedData = normalizeEventData(eventData, eventConfig.dimensions);
    const dataToSend = {
      ...normalizedData,
    };
    const userDataWithMqttValues = {
      ...gtmUserData,
      ...normalizedData,
    };
    gtmEvent({
      hash: eventConfig.hash,
      eventType: eventConfig.eventType,
      dimensions: eventConfig.dimensions,
      userData: userDataWithMqttValues,
      data: dataToSend,
    });
  } catch (error) {
    console.error('MQTT GTM Event: Error dispatching event', error);
  }
};

async function initializeMqtt(gtmUserData) {
  let auth;
  let client;
  const host = import.meta.env.REACT_APP_BACKEND_SERVICE_HOST;
  const mqttComponentInstance = new MqttComponent(authInterceptor);
  const { idToken } = getAuthTokensData();
  const { vrioCustomerId, iat } = jwtDecode(idToken);

  resetMqttAttempt();

  try {
    const attemptNumber = incrementMqttAttempt();
    dispatchMqttEvent(
      gtmMqttDimensions.connection.attempt,
      {
        v_attempt: (attemptNumber || 1).toString(),
        v_date: formatMqttDate(),
      },
      gtmUserData,
    );

    auth = await mqttComponentInstance.authMqtt(
      idToken,
      host,
      APP_THEME,
      document.location.origin.includes('preprod.directvgo') ? SERVICES_CLIENT_ID_PREPROD : SERVICES_CLIENT_ID, // clientId
      ENVIRONMENT,
    );

    const connectResult = mqttComponentInstance.connectMqtt(
      auth?.clientId,
      auth?.url,
      vrioCustomerId,
      (iat * 1000), // date in miliseconds;
      refreshIdToken,
      MQTT_REALTIME_ENTITLEMENT_FLAG,
      MQTT_FLAG,
    );

    client = connectResult instanceof Promise ? (await connectResult) : connectResult;

    if (client && auth?.url) {
      dispatchMqttEvent(
        gtmMqttDimensions.connection.success,
        {
          v_host: auth?.url || host || 'unknown',
          v_date: formatMqttDate(),
        },
        gtmUserData,
      );

      if (typeof client === 'object' && client !== null) {
        const setupEventTracking = (mqttClient) => {
          const subscribeKey = Symbol('originalSubscribe');
          if (mqttClient[subscribeKey]) return;

          const originalSubscribe = mqttClient.subscribe;

          if (typeof originalSubscribe === 'function') {
            // Wrapping subscribe method to add GTM tracking
            // eslint-disable-next-line no-param-reassign
            mqttClient.subscribe = function subscribeWrapper(...args) {
              let topic = 'unknown';
              const [firstArg] = args;
              if (typeof firstArg === 'string') {
                topic = firstArg || 'unknown';
              } else if (Array.isArray(firstArg) && firstArg.length > 0) {
                const [firstTopic] = firstArg;
                topic = typeof firstTopic === 'string'
                  ? (firstTopic || 'unknown')
                  : (firstTopic?.topic || firstTopic?.toString() || 'unknown');
              } else if (firstArg) {
                topic = firstArg?.topic || firstArg?.toString() || 'unknown';
              }

              dispatchMqttEvent(
                gtmMqttDimensions.subscription.attempt,
                {
                  v_topic: topic || 'unknown',
                  v_date: formatMqttDate(),
                },
                gtmUserData,
              );

              const result = originalSubscribe.apply(this, args);

              if (result instanceof Promise) {
                result
                  .catch((error) => {
                    dispatchMqttEvent(
                      gtmMqttDimensions.subscription.failure,
                      {
                        v_topic: topic || 'unknown',
                        v_date: formatMqttDate(),
                      },
                      gtmUserData,
                    );
                    console.error('MQTT subscription error:', error);
                  });
              } else if (result && typeof result.then === 'function') {
                result
                  .catch((error) => {
                    dispatchMqttEvent(
                      gtmMqttDimensions.subscription.failure,
                      {
                        v_topic: topic || 'unknown',
                        v_date: formatMqttDate(),
                      },
                      gtmUserData,
                    );
                    console.error('MQTT subscription error:', error);
                  });
              }

              return result;
            };
            // Store reference to prevent double wrapping
            // eslint-disable-next-line no-param-reassign
            mqttClient[subscribeKey] = originalSubscribe;
          }

          if (typeof mqttClient.on === 'function') {
            // Remover listeners anteriores para evitar duplicatas
            mqttClient.removeAllListeners?.('message');
            mqttClient.removeAllListeners?.('error');
            mqttClient.removeAllListeners?.('subscribe');

            mqttClient.on('message', (topic, message) => {
              const payloadString = typeof message === 'string'
                ? message
                : (message?.toString() || JSON.stringify(message) || '');

              dispatchMqttEvent(
                gtmMqttDimensions.message.received,
                {
                  v_topic: topic || 'unknown',
                  v_payload: truncatePayload(payloadString),
                  v_date: formatMqttDate(),
                },
                gtmUserData,
              );
            });

            mqttClient.on('error', (error) => {
              dispatchMqttEvent(
                gtmMqttDimensions.connection.failure,
                {
                  v_host: auth?.url || host || 'unknown',
                  v_reason: error?.message || error?.toString() || 'Unknown error',
                  v_date: formatMqttDate(),
                },
                gtmUserData,
              );
            });

            mqttClient.on('subscribe', (granted) => {
              if (Array.isArray(granted) && granted.length > 0) {
                granted.forEach((grant) => {
                  dispatchMqttEvent(
                    gtmMqttDimensions.subscription.success,
                    {
                      v_topic: grant?.topic || 'unknown',
                      v_date: formatMqttDate(),
                    },
                    gtmUserData,
                  );
                });
              }
            });
          }
        };

        try {
          setupEventTracking(client);
        } catch (trackingError) {
          console.warn('Could not set up MQTT event tracking:', trackingError);
        }
      }
    }
  } catch (error) {
    dispatchMqttEvent(
      gtmMqttDimensions.connection.failure,
      {
        v_host: auth?.url || host || 'unknown',
        v_reason: error?.message || error?.toString() || 'Unknown error',
        v_date: formatMqttDate(),
      },
      gtmUserData,
    );
    console.error('MQTT connection error:', error);
  }

  return client;
}

export function Provider({ children }) {
  const [contextValue, setContextValue] = useState('');
  const initializedRef = useRef(false);
  const isLoggedIn = isUserLoggedIn();
  const { gtmUserData } = useGtmContext();
  const selectedProfile = useSelector((state) => state.profile.selectedProfile);
  const { idToken } = getAuthTokensData();

  useEffect(() => {
    const initialize = async () => {
      // Evitar múltiplas inicializações
      if (initializedRef.current || contextValue) {
        return;
      }

      if (!MQTT_FLAG || !isUserLoggedIn() || !idToken || !gtmUserData) {
        return;
      }

      if (!gtmUserData.v_user_id || !gtmUserData.v_app_name) {
        console.warn('MQTT: Skipping initialization - gtmUserData incomplete');
        return;
      }

      initializedRef.current = true;

      // eslint-disable-next-line no-console
      console.log('MQTT: Initializing with gtmUserData:', {
        hasUserId: !!gtmUserData.v_user_id,
        hasAppName: !!gtmUserData.v_app_name,
        hasProfileId: !!gtmUserData.v_profile_id,
        keys: Object.keys(gtmUserData),
      });

      try {
        const result = await initializeMqtt(gtmUserData);
        setContextValue(result);
      } catch (error) {
        console.error('MQTT initialization error:', error);
        initializedRef.current = false; // Reset em caso de erro
      }
    };

    initialize();
  }, [isLoggedIn, contextValue, gtmUserData, selectedProfile, idToken]);

  return (
    <MqttContext.Provider value={contextValue}>
      {children}
    </MqttContext.Provider>
  );
}

Provider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useMqttContext() {
  return useContext(MqttContext);
}
